/*
 * Shared inbox data for conversation badges, invitations and active chapter summaries.
 * Created by the application shell for the Inbox and Reviews navigation menus.
 * Coalesces authenticated refreshes and live hints so each disclosure need not read independently.
 * Example: const inbox = context.service(createInboxSource);
 *          const unsubscribe = inbox.subscribe(() => showInbox(inbox.get()));
 */

import { createStore } from '../../runtime/store.js';
import { createLifetime } from '../../runtime/lifetime.js';
import { ResourceController } from '../../runtime/resource-controller.js';
import { createChapterActivityRuntime } from '../../streaming/chapter-activity.js';
import { staffWorkCapsuleState } from '../ui/staff-work-list.js';
import { mergeConversationSnapshot } from '../../lib/messaging/message-state.js';
import {
  hasInboxActivity,
  inboxEventHasUnread,
  isInboxConversationEvent
} from '../../lib/messaging/inbox-unread.js';

export function createInboxSource(context) {
  const { auth, api, state: store, reads, stream, sync } = context.services;
  const activity = context.service(createChapterActivityRuntime);
  const life = createLifetime();
  const eligible = () => !!auth.getAuthContext() && !auth.needsEmailVerification();
  const snapshot = createStore({
    identity: auth.getAuthContext(),
    enabled: eligible(),
    latestSeqs: new Map(),
    conversations: store.get('msg.conversations') || [],
    invitations: store.get('msg.invitations') || [],
    hasUnread: !!store.get('msg.hasUnread'),
    chapters: { chapters: [], loaded: false, dot: false, pulse: false },
    capsule: staffWorkCapsuleState(),
    staffWork: null
  });
  let resource;
  let publishing = false;

  function present(patch = {}) {
    if (life.disposed || context.signal.aborted) return;
    const state = { ...snapshot.get(), ...patch };
    const dot =
      state.chapters.dot ||
      hasInboxActivity({
        invitations: state.invitations,
        serverHasUnread: state.hasUnread,
        latestSeqs: state.latestSeqs,
        getReadSeq: reads.getSeq
      });
    snapshot.update({
      ...state,
      dot,
      pulse: state.chapters.pulse,
      label: state.chapters.pulse
        ? 'Chapters and chats — Your Turn, not yet acknowledged'
        : dot
          ? 'Chapters and chats — New activity'
          : 'Chapters and chats'
    });
  }

  function createResource() {
    resource?.dispose();
    resource = new ResourceController(context, {
      context,
      active: eligible,
      watch: false,
      debounceMs: 50,
      retryOnError: false,

      async load(signal) {
        const identity = auth.getAuthContext();
        const snapshot = await Promise.all([
          api.get('/api/reads', { signal }),
          api.get('/api/conversations?order=inbox&surface=menu', { signal }),
          api.get('/api/conversations/invitations', { signal }),
          api.get('/api/staff-work?order=oldest', { signal })
        ]);
        return { identity, snapshot };
      },

      receive: accept
    });
  }

  function refresh() {
    return eligible() && !life.disposed ? resource.invalidate() : Promise.resolve();
  }

  function session() {
    const identity = auth.getAuthContext();
    const enabled = eligible();
    const changed = snapshot.get().identity !== identity;
    if (changed || snapshot.get().enabled !== enabled) {
      createResource();
      present({
        identity,
        enabled,
        ...(changed
          ? { latestSeqs: new Map(), staffWork: null, capsule: staffWorkCapsuleState() }
          : {})
      });
    }
    if (enabled) void refresh();
  }

  function event(type, data) {
    const { conversation_id, seq } = data;
    const conversations = [...(store.get('msg.conversations') || [])];
    if (!isInboxConversationEvent({ type, conversationId: conversation_id, conversations })) return;
    const state = snapshot.get();
    if (seq <= (state.latestSeqs.get(conversation_id) || 0)) return;
    state.latestSeqs.set(conversation_id, seq);
    const hasUnread = inboxEventHasUnread({
      conversationId: conversation_id,
      seq,
      getReadSeq: reads.getSeq,
      actorId: auth.getUser()?.id,
      authorId: data.author_id
    });
    const existing = conversations.find((conversation) => conversation.id === conversation_id);
    if (existing) {
      existing.latest_seq = seq;
      existing.has_unread = hasUnread;
      store.set('msg.conversations', conversations);
    }
    if (hasUnread) store.set('msg.hasUnread', true);
    present();
    void refresh();
  }

  function accept({ identity: origin, snapshot: responses }) {
    if (!eligible() || origin !== auth.getAuthContext()) return;
    const [readState, conversations, invitations, staffWork] = responses;
    for (const entry of readState.data || [])
      reads.adoptSeq(entry.conversation_id, entry.last_read_seq);
    const previousSeqs = snapshot.get().latestSeqs;
    const summaries = mergeConversationSnapshot(
      store.get('msg.conversations'),
      conversations.data || []
    ).map((conversation) => ({
      ...conversation,
      latest_seq: Math.max(conversation.latest_seq || 0, previousSeqs.get(conversation.id) || 0)
    }));
    const latestSeqs = new Map(
      summaries.map((conversation) => [conversation.id, conversation.latest_seq || 0])
    );
    publishing = true;
    try {
      store.set('msg.conversations', summaries);
      store.set('msg.hasUnread', !!conversations.meta?.has_unread);
      store.set('msg.invitations', invitations.data || []);
    } finally {
      publishing = false;
    }
    present({
      latestSeqs,
      conversations: summaries,
      hasUnread: !!conversations.meta?.has_unread,
      invitations: invitations.data || [],
      staffWork,
      capsule: staffWorkCapsuleState(staffWork.meta, snapshot.get().capsule)
    });
    context.window.dispatchEvent(new context.window.Event('inbox-reconciled'));
  }

  present();
  createResource();
  for (const key of ['msg.conversations', 'msg.invitations', 'msg.hasUnread'])
    life.own(
      store.subscribe(
        key,
        life.guard(() => {
          if (!publishing)
            present({
              conversations: store.get('msg.conversations') || [],
              invitations: store.get('msg.invitations') || [],
              hasUnread: !!store.get('msg.hasUnread')
            });
        })
      )
    );
  life.own(activity.watchChapterActivity(life.guard((chapters) => present({ chapters }))));
  life.own(auth.subscribeAuth(life.guard(session)));
  for (const [name, handler] of [
    ['new_message', (data) => event('new_message', data)],
    ['post_log', (data) => event('post_log', data)],
    ['staff_work_changed', refresh],
    ['reconcile', refresh]
  ]) {
    const callback = life.guard(handler);
    stream.on(name, callback);
    life.own(() => stream.off(name, callback));
  }
  const listen = (target, name, callback) => {
    const handler = life.guard(callback);
    target.addEventListener(name, handler);
    life.own(() => target.removeEventListener(name, handler));
  };
  listen(context.window, 'last-seen-changed', () => present());
  listen(context.window, 'inbox-unread-synced', (event) =>
    store.set('msg.hasUnread', !!event.detail?.hasUnread)
  );
  listen(context.window, 'pageshow', refresh);
  listen(context.document, 'visibilitychange', () => {
    if (context.document.visibilityState === 'visible') void refresh();
  });
  life.own(
    sync.onUIChange(
      life.guard((change) => {
        if (change.kind === 'read' || change.kind === 'inbox') void refresh();
      })
    )
  );
  life.own(() => resource.dispose());
  const dispose = () => {
    forget();
    life.dispose();
  };
  const forget = context.own(dispose);
  if (eligible()) void refresh();

  return {
    get: snapshot.get,
    subscribe: snapshot.subscribe,
    dispose,
    refresh,
    refreshChapters: activity.chapterActivity.refresh,

    updateMeta(meta, origin = auth.getAuthContext()) {
      if (life.disposed || !origin || origin !== auth.getAuthContext()) return;
      const previous = snapshot.get().capsule;
      const capsule = staffWorkCapsuleState(meta, previous);
      if (Object.keys(capsule).some((name) => capsule[name] !== previous[name]))
        present({ capsule });
    },

    removeConversation(id) {
      if (life.disposed) return;
      snapshot.get().latestSeqs.delete(id);
      store.set(
        'msg.conversations',
        (store.get('msg.conversations') || []).filter((item) => item.id !== id)
      );
    }
  };
}
